import styled from 'styled-components';

/**
 * Vertical layout row for a labelled form field — label on top, control
 * below, optional helper / error text underneath. Provides the 18px gap
 * between adjacent fields inside a modal body; the last field's bottom
 * margin collapses so the modal's intrinsic padding handles the trailing
 * gutter.
 */
export const DialogField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DialogFieldLabel = styled.label`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
`;

export const DialogFieldError = styled.div`
  font-size: 12.5px;
  // Soft salmon — keeps the helper readable against the dialog's deep
  // surface without competing with the error halo around the input
  // itself. Falls back to a literal so the value is stable when the
  // theme doesn't expose a dedicated error-on-surface token.
  color: ${({ theme: { colors = {} } }) => colors.error ?? '#F2B8A0'};
  margin-top: 4px;
`;

/**
 * Optional intro paragraph that sits above the first {@link DialogField}.
 * Use `<strong>` inside the copy to call out a name / value in the
 * dominant surface ink — the muted body keeps the eye on the bolded
 * accent. The trailing 18px gutter collapses when this is the only
 * child so single-paragraph dialogs (e.g. duplicate-confirm) don't
 * inherit an extra strip of whitespace.
 */
export const DialogSub = styled.p`
  margin: 0 0 18px;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C9C5D5'};
  text-wrap: pretty;

  & strong {
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
    font-weight: 600;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

/**
 * Footer row container — typically a Cancel / Confirm pair of buttons.
 * The {@link Modal}'s `footer` slot already provides the gutter to the
 * dialog body; this only contributes the inter-button gap.
 */
export const DialogFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/**
 * Pill-shaped multi-line text input — matches `TextField appearance="pill"
 * size="lg"` for input fields, but as a `<textarea>`. Used in modal
 * dialogs where the user enters a free-form note (message, comment,
 * instructions).
 */
export const DialogTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 20px;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainer ?? '#1F1E27'};
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  border: 0;
  border-radius: 22px;
  outline: none;
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 96px;
  transition: background 120ms ease;

  &::placeholder {
    color: ${({ theme: { colors = {} } }) =>
      colors.onSurfaceVariant ?? '#C9C5D5'};
  }

  &:focus {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
